import React, { useEffect, useState, useMemo } from "react";
import { api, eur, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Clock, Copy, Archive, ArchiveRestore, GripVertical, X, ChevronDown, ChevronRight, Package, Search as SearchIcon, X as ClearIcon, MoreVertical } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ApplyTemplatePicker, SaveAsTemplateButton } from "@/pages/AdminModifierTemplates";

const WEEKDAYS = [
  { v: 0, label: "Lun" }, { v: 1, label: "Mar" }, { v: 2, label: "Mer" },
  { v: 3, label: "Gio" }, { v: 4, label: "Ven" }, { v: 5, label: "Sab" }, { v: 6, label: "Dom" },
];

// --------- Slot formatting helpers ---------
function formatDaysGrouped(days) {
  if (!days || days.length === 0 || days.length === 7) return "Tutti i giorni";
  const sorted = [...days].sort((a, b) => a - b);
  const groups = [];
  let start = sorted[0], prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === prev + 1) { prev = sorted[i]; continue; }
    groups.push([start, prev]); start = prev = sorted[i];
  }
  groups.push([start, prev]);
  return groups.map(([s, e]) => s === e ? WEEKDAYS[s].label : `${WEEKDAYS[s].label}-${WEEKDAYS[e].label}`).join(", ");
}

function slotIcon(from, to) {
  // Automatic sun/moon: sun if fully in 06:00-18:00 window, moon otherwise.
  if (!from && !to) return "";
  const toMin = (t) => { if (!t) return null; const [h, m] = t.split(":"); return +h * 60 + +m; };
  const s = toMin(from), e = toMin(to);
  if (s !== null && e !== null) {
    if (e >= s && s >= 360 && e <= 1080) return "☀";
    return "🌙";
  }
  return "";
}

function formatSlot(slot) {
  const icon = slotIcon(slot.from_time, slot.to_time);
  const days = formatDaysGrouped(slot.days);
  const t = (slot.from_time || slot.to_time)
    ? `${slot.from_time || "00:00"}-${slot.to_time || "23:59"}${slot.from_time && slot.to_time && slot.from_time > slot.to_time ? " (+1g)" : ""}`
    : "tutto il giorno";
  return { icon, text: `${days} ${t}`.trim() };
}

function slotsPreview(timeSlots, af, at, days) {
  // Return an array of {icon,text} lines, or [{icon:"", text:"Sempre disponibile"}]
  if (Array.isArray(timeSlots) && timeSlots.length > 0) {
    return timeSlots.map(formatSlot);
  }
  const legacyAllDays = !days || days.length === 0 || days.length === 7;
  const legacyNoTime = !af && !at;
  if (legacyAllDays && legacyNoTime) return [{ icon: "", text: "Sempre disponibile" }];
  return [formatSlot({ from_time: af, to_time: at, days: days || [] })];
}

// --------- Slot overlap validation (mirror of backend logic) ---------
function slotsOverlapError(slots) {
  if (!slots || slots.length < 2) return null;
  const toMin = (t) => { if (!t) return null; const [h, m] = t.split(":"); return +h * 60 + +m; };
  const intervals = []; // {day,s,e,idx}
  slots.forEach((s, idx) => {
    const days = (s.days && s.days.length) ? s.days : [0, 1, 2, 3, 4, 5, 6];
    let a = toMin(s.from_time), b = toMin(s.to_time);
    if (a === null && b === null) { days.forEach((d) => intervals.push({ day: d, s: 0, e: 1440, idx })); return; }
    if (a === null) a = 0;
    if (b === null) b = 1440;
    if (b === a) b = 1440;
    if (b > a) days.forEach((d) => intervals.push({ day: d, s: a, e: b, idx }));
    else days.forEach((d) => {
      intervals.push({ day: d, s: a, e: 1440, idx });
      intervals.push({ day: (d + 1) % 7, s: 0, e: b, idx });
    });
  });
  const byDay = {};
  intervals.forEach((it) => { (byDay[it.day] = byDay[it.day] || []).push(it); });
  for (const d of Object.keys(byDay)) {
    const arr = byDay[d].sort((x, y) => x.s - y.s);
    for (let i = 0; i < arr.length; i++) for (let j = i + 1; j < arr.length; j++) {
      if (arr[i].idx === arr[j].idx) continue;
      if (arr[j].s < arr[i].e) return { i: arr[i].idx, j: arr[j].idx };
      break;
    }
  }
  return null;
}

// -----------------------------------------------------------------------------
// Multi-time-slot editor (used by CategoryDialog and ProductDialog)

function MultiTimeSlotFields({ slots, onChange, testidPrefix }) {
  // Ensure each slot has a stable _key for React & DnD (never sent to backend).
  const list = React.useMemo(() => {
    return (slots || []).map((s, i) => s._key ? s : { ...s, _key: `slot-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}` });
  }, [slots]);

  const emit = (arr) => onChange(arr.map(({ _key, ...rest }) => rest)); // strip _key on emit
  const addSlot = () => emit([...list, { from_time: null, to_time: null, days: [] }]);
  const setSlot = (i, patch) => emit(list.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const dupSlot = (i) => {
    const src = list[i];
    const copy = { from_time: src.from_time || null, to_time: src.to_time || null, days: [...(src.days || [])] };
    emit([...list.slice(0, i + 1), copy, ...list.slice(i + 1)]);
  };
  const rmSlot = (i) => emit(list.filter((_, idx) => idx !== i));
  const toggleDay = (i, d) => {
    const cur = new Set(list[i].days || []);
    cur.has(d) ? cur.delete(d) : cur.add(d);
    setSlot(i, { days: Array.from(cur).sort((a, b) => a - b) });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
                             useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = list.findIndex((s) => s._key === active.id);
    const newIdx = list.findIndex((s) => s._key === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    emit(arrayMove(list, oldIdx, newIdx));
  };

  const overlap = slotsOverlapError(list);

  return (
    <div className="rounded-xl border border-sand-300 p-3 bg-sand-50/40" data-testid={`${testidPrefix}-slots`}>
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1"><Clock className="w-4 h-4" /> Fasce orarie ({list.length})</Label>
        <div className="flex items-center gap-2">
          {list.length > 0 && (
            <button type="button" onClick={() => emit([])} className="text-xs text-bronze-600 hover:underline"
                    data-testid={`${testidPrefix}-slots-clear`}>Sempre disponibile</button>
          )}
          <Button type="button" size="sm" variant="outline" onClick={addSlot}
                  data-testid={`${testidPrefix}-add-slot`}>
            <Plus className="w-3 h-3 mr-1" />Aggiungi fascia
          </Button>
        </div>
      </div>
      {list.length === 0 && (
        <div className="mt-2 text-xs text-ink-500 bg-white rounded-lg border border-dashed border-sand-300 p-3 text-center">
          Nessuna fascia impostata = <b>sempre disponibile</b>. Clicca "Aggiungi fascia" per limitare l'orario.
        </div>
      )}
      {list.length > 1 && (
        <div className="mt-2 text-[11px] text-ink-500 italic">Trascina la maniglia per riordinare le fasce. L'ordine viene rispettato nel menu cliente.</div>
      )}
      {overlap && (
        <div className="mt-2 text-xs bg-red-50 border border-red-300 text-red-800 rounded-lg px-2 py-1.5" data-testid={`${testidPrefix}-slots-overlap`}>
          ⚠ Le fasce <b>#{overlap.i + 1}</b> e <b>#{overlap.j + 1}</b> si sovrappongono o coincidono. Correggi giorni od orari.
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={list.map((s) => s._key)} strategy={verticalListSortingStrategy}>
          <div className="mt-2 space-y-2">
            {list.map((s, i) => (
              <SortableSlotRow key={s._key} slot={s} index={i} testidPrefix={testidPrefix}
                               onFromChange={(v) => setSlot(i, { from_time: v })}
                               onToChange={(v) => setSlot(i, { to_time: v })}
                               onToggleDay={(d) => toggleDay(i, d)}
                               onDup={() => dupSlot(i)}
                               onDel={() => rmSlot(i)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableSlotRow({ slot, index, testidPrefix, onFromChange, onToChange, onToggleDay, onDup, onDel }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slot._key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const preview = formatSlot(slot);
  return (
    <div ref={setNodeRef} style={style}
         className="rounded-lg border border-sand-300 p-2 bg-white space-y-2"
         data-testid={`${testidPrefix}-slot-${index}`}>
      <div className="flex items-center gap-2">
        <button type="button" {...attributes} {...listeners}
                data-testid={`${testidPrefix}-slot-${index}-drag`}
                className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-sand-200 touch-none" title="Trascina per riordinare">
          <GripVertical className="w-4 h-4 text-ink-500" />
        </button>
        <span className="text-xs text-ink-500 font-mono w-6">#{index + 1}</span>
        <div className="flex-1 grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px]">Dalle</Label>
            <Input type="time" value={slot.from_time || ""} onChange={(e) => onFromChange(e.target.value || null)}
                   data-testid={`${testidPrefix}-slot-${index}-from`} />
          </div>
          <div>
            <Label className="text-[11px]">Alle {slot.from_time && slot.to_time && slot.from_time > slot.to_time && <span className="text-bronze-600">(oltre mezzanotte)</span>}</Label>
            <Input type="time" value={slot.to_time || ""} onChange={(e) => onToChange(e.target.value || null)}
                   data-testid={`${testidPrefix}-slot-${index}-to`} />
          </div>
        </div>
        <button type="button" onClick={onDup} className="p-1 rounded hover:bg-sand-100"
                data-testid={`${testidPrefix}-slot-${index}-dup`} title="Duplica fascia"><Copy className="w-4 h-4" /></button>
        <button type="button" onClick={onDel} className="p-1 rounded text-red-700 hover:bg-red-50"
                data-testid={`${testidPrefix}-slot-${index}-del`} title="Elimina fascia"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-1 items-center">
        {WEEKDAYS.map((d) => {
          const on = (slot.days || []).includes(d.v);
          return (
            <button key={d.v} type="button" onClick={() => onToggleDay(d.v)}
                    data-testid={`${testidPrefix}-slot-${index}-day-${d.v}`}
                    className={`px-2 py-0.5 rounded-full text-xs border ${on ? "border-bronze-600 bg-bronze-600 text-white" : "border-sand-300 bg-white"}`}>
              {d.label}
            </button>
          );
        })}
        <span className="text-[11px] text-ink-500 self-center ml-1">Nessun giorno = tutti i giorni</span>
      </div>
      <div className="text-[11px] text-ink-500 border-t border-sand-200 pt-1" data-testid={`${testidPrefix}-slot-${index}-preview`}>
        Anteprima: <b>{preview.icon} {preview.text}</b>
      </div>
    </div>
  );
}

export default function AdminMenu() {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [mods, setMods] = useState([]);

  const load = async () => {
    const [c, p, m] = await Promise.all([
      api.get("/admin/categories"), api.get("/admin/products"), api.get("/admin/modifier-groups")
    ]);
    setCats(c.data); setProds(p.data); setMods(m.data);
  };
  useEffect(() => { load(); }, []);

  const toggleAvailability = async (p) => {
    try { await api.patch(`/admin/products/${p.id}/availability?available=${!p.available}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteProd = async (id) => {
    if (!window.confirm("Eliminare/archiviare il prodotto? Se referenziato da ordini storici, verrà solo archiviato.")) return;
    try { await api.delete(`/admin/products/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteCat = async (id) => {
    if (!window.confirm("Eliminare/archiviare la categoria? Se contiene prodotti, verrà solo archiviata.")) return;
    try { await api.delete(`/admin/categories/${id}`); load(); toast.success("Categoria elaborata"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const duplicateProd = async (id) => { try { await api.post(`/admin/products/${id}/duplicate`); load(); toast.success("Prodotto duplicato"); } catch (e) { toast.error(formatApiError(e)); } };
  const duplicateCat = async (id) => { try { await api.post(`/admin/categories/${id}/duplicate`); load(); toast.success("Categoria duplicata"); } catch (e) { toast.error(formatApiError(e)); } };
  const archiveCat = async (c) => { try { await api.post(`/admin/categories/${c.id}/${c.archived ? "unarchive" : "archive"}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const archiveProd = async (p) => { try { await api.post(`/admin/products/${p.id}/${p.archived ? "unarchive" : "archive"}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div>
      <div className="eyebrow">Gestione</div>
      <h1 className="text-4xl mt-1">Menu</h1>

      <section className="card-elevated p-5 mt-6">
        <div className="flex justify-between items-center">
          <div className="eyebrow">Categorie ({cats.length})</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-500 hidden sm:inline">Trascina per riordinare</span>
            <CategoryDialog onSaved={load} allCategories={cats} />
          </div>
        </div>
        <CategoriesReorderableList cats={cats} onReload={load}
                                    onDuplicate={duplicateCat} onArchive={archiveCat}
                                    onDelete={deleteCat} />
      </section>

      <section className="card-elevated p-5 mt-6">
        <div className="flex justify-between items-center">
          <div className="eyebrow">Prodotti ({prods.length})</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-ink-500 hidden sm:inline">Trascina per riordinare</span>
            <ProductDialog categories={cats} modifiers={mods} onSaved={load} />
          </div>
        </div>
        <ProductsByCategory cats={cats} prods={prods} mods={mods} onReload={load}
                            onToggleAvailability={toggleAvailability}
                            onDuplicate={duplicateProd} onArchive={archiveProd} onDelete={deleteProd}
                            duplicateCat={duplicateCat} archiveCat={archiveCat} deleteCat={deleteCat} />
      </section>

      <ModifierGroupsSection groups={mods} onReload={load} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// CATEGORIES DRAG-DROP REORDER + HIERARCHY REPARENT
//
// Hierarchy invariants (enforced by backend + client):
//  - Max 1 level of nesting (a parent is always a root).
//  - No self-parent, no cycles.
//  - A category with children cannot become a child (must first flatten).
// Cross-container drag persists via POST /admin/categories/hierarchy.

function CategoriesReorderableList({ cats, onReload, onDuplicate, onArchive, onDelete }) {
  const [items, setItems] = useState(cats);
  useEffect(() => { setItems(cats); }, [cats]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const roots = useMemo(
    () => items.filter((c) => !c.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [items],
  );
  const childrenOf = (pid) =>
    items.filter((c) => c.parent_id === pid).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (rid) => setExpanded((s) => ({ ...s, [rid]: !s[rid] }));

  // Container id resolution.
  const containerOf = (id) => {
    if (!id) return null;
    if (id === "container:root") return "root";
    if (typeof id === "string" && id.startsWith("container:")) return id.slice("container:".length);
    const c = items.find((x) => x.id === id);
    if (!c) return null;
    return c.parent_id || "root";
  };

  const persist = async (changed) => {
    if (!changed.length) return;
    try {
      await api.post("/admin/categories/hierarchy", {
        items: changed.map((c) => ({ id: c.id, parent_id: c.parent_id || null, sort_order: c.sort_order || 0 })),
      });
      toast.success("Gerarchia aggiornata");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
      onReload();
    }
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const src = containerOf(active.id);
    const dst = containerOf(over.id);
    if (!src || !dst) return;
    const dragged = items.find((c) => c.id === active.id);
    if (!dragged) return;

    // Guardrails.
    const draggedHasChildren = items.some((c) => c.parent_id === active.id);
    if (dst !== "root" && draggedHasChildren) {
      toast.error("Categoria con sottocategorie: rimuovile prima di renderla figlia");
      return;
    }
    if (dst !== "root") {
      const dstCat = items.find((c) => c.id === dst);
      if (!dstCat) return;
      if (dstCat.parent_id) {
        toast.error("Solo un livello di nidificazione consentito");
        return;
      }
    }

    let mutated = items.map((c) => ({ ...c }));

    if (src === dst) {
      // Reorder within same container.
      const siblings = (src === "root" ? roots : childrenOf(src)).slice();
      const oldIdx = siblings.findIndex((c) => c.id === active.id);
      let newIdx;
      if (typeof over.id === "string" && over.id.startsWith("container:")) {
        newIdx = siblings.length - 1;
      } else {
        newIdx = siblings.findIndex((c) => c.id === over.id);
      }
      if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
      const reordered = arrayMove(siblings, oldIdx, newIdx);
      reordered.forEach((c, i) => {
        const t = mutated.find((m) => m.id === c.id);
        if (t) t.sort_order = i * 10;
      });
    } else {
      // Cross-container: reparent + sort.
      const newParent = dst === "root" ? null : dst;
      const dstSiblings = (dst === "root" ? roots : childrenOf(dst)).slice();
      let newIdx;
      if (typeof over.id === "string" && over.id.startsWith("container:")) {
        newIdx = dstSiblings.length;
      } else {
        newIdx = dstSiblings.findIndex((c) => c.id === over.id);
        if (newIdx < 0) newIdx = dstSiblings.length;
      }
      // Remove from source.
      const srcSiblings = (src === "root" ? roots : childrenOf(src)).filter((c) => c.id !== active.id);
      srcSiblings.forEach((c, i) => {
        const t = mutated.find((m) => m.id === c.id);
        if (t) t.sort_order = i * 10;
      });
      // Insert into destination.
      const dstNew = [
        ...dstSiblings.slice(0, newIdx),
        { ...dragged, parent_id: newParent },
        ...dstSiblings.slice(newIdx),
      ];
      dstNew.forEach((c, i) => {
        const t = mutated.find((m) => m.id === c.id);
        if (t) { t.parent_id = newParent; t.sort_order = i * 10; }
      });
      // Auto-expand target root so the user sees the drop.
      if (newParent) setExpanded((s) => ({ ...s, [newParent]: true }));
    }

    // Diff.
    const changed = mutated.filter((nc) => {
      const orig = items.find((o) => o.id === nc.id);
      if (!orig) return false;
      return (orig.sort_order || 0) !== (nc.sort_order || 0)
          || (orig.parent_id || null) !== (nc.parent_id || null);
    });
    setItems(mutated);
    await persist(changed);
  };

  const setParent = async (catId, newParentId) => {
    // Promote/demote via dropdown UI. Applies same guardrails as DnD.
    const cat = items.find((c) => c.id === catId);
    if (!cat) return;
    const targetParent = newParentId || null;
    if (targetParent === (cat.parent_id || null)) return;
    if (targetParent === catId) {
      toast.error("Una categoria non può essere padre di se stessa");
      return;
    }
    const hasChildren = items.some((c) => c.parent_id === catId);
    if (targetParent && hasChildren) {
      toast.error("Categoria con sottocategorie: rimuovile prima di renderla figlia");
      return;
    }
    if (targetParent) {
      const p = items.find((c) => c.id === targetParent);
      if (!p || p.parent_id) {
        toast.error("Solo un livello di nidificazione consentito");
        return;
      }
    }
    const dstSiblings = items.filter((c) => (c.parent_id || null) === targetParent && c.id !== catId);
    const newSortOrder = dstSiblings.length * 10;
    await persist([{ id: catId, parent_id: targetParent, sort_order: newSortOrder }]);
  };

  return (
    <div className="mt-3">
      <div className="text-xs text-ink-500 mb-2">
        <span className="hidden sm:inline">Trascina le categorie per riordinarle. Rilascia dentro un'altra categoria per creare una sottocategoria; sopra la zona "categorie principali" per promuoverla a livello principale.</span>
        <span className="sm:hidden">Trascina per riordinare o annidare. Menu ⋮ per spostare rapidamente.</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <RootCategoryDropZone />
        <SortableContext items={roots.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <div className="grid gap-2" data-testid="cat-hierarchy-root">
            {roots.map((c) => (
              <RootCategoryRow key={c.id} c={c} allCats={items}
                                childCats={childrenOf(c.id)}
                                isExpanded={!!expanded[c.id]}
                                onToggleExpanded={() => toggleExpanded(c.id)}
                                onReload={onReload}
                                onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete}
                                onSetParent={setParent} />
            ))}
            {roots.length === 0 && (
              <div className="card-elevated p-6 text-center text-ink-500">Nessuna categoria. Creane una con "+ Nuova categoria".</div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function RootCategoryDropZone() {
  const { setNodeRef, isOver } = useDroppable({ id: "container:root" });
  return (
    <div ref={setNodeRef} data-testid="cat-drop-root"
         className={`mb-2 rounded-xl border-2 border-dashed px-3 py-2 text-xs transition-colors ${isOver ? "border-bronze-600 bg-bronze-50 text-bronze-700" : "border-sand-300 text-ink-500"}`}>
      {isOver ? "Rilascia qui → promuovi a categoria principale" : "Zona categorie principali · trascina qui una sottocategoria per promuoverla"}
    </div>
  );
}

function RootCategoryRow({ c, allCats, childCats, isExpanded, onToggleExpanded,
                          onReload, onDuplicate, onArchive, onDelete, onSetParent }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `container:${c.id}` });

  const totalProd = 0; // filled by Contenuti section, header is category-only
  const activeChildren = childCats.filter((x) => !x.archived).length;

  return (
    <div ref={setNodeRef} style={style} data-testid={`cat-row-${c.id}`}
         className={`rounded-xl border-2 ${isOver ? "border-bronze-500 bg-bronze-50/40" : "border-sand-300"} bg-white ${c.archived ? "opacity-50" : ""} transition-colors`}>
      <div className="flex items-start gap-2 px-3 py-2 text-sm">
        <button {...attributes} {...listeners} data-testid={`drag-cat-${c.id}`}
                className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-sand-200 touch-none mt-0.5">
          <GripVertical className="w-4 h-4 text-ink-500" />
        </button>
        <button onClick={onToggleExpanded} data-testid={`cat-tree-toggle-${c.id}`}
                className="p-0.5 rounded hover:bg-sand-100 mt-0.5" aria-label="Espandi sottocategorie">
          <ChevronRight className={`w-4 h-4 transition-transform duration-150 ${isExpanded ? "rotate-90" : ""}`} />
        </button>
        <div className="min-w-0 flex-1">
          <div><b className="font-serif text-base">▾ {c.name}</b> <span className="text-ink-500">· {c.department}</span>
            {c.archived && <span className="ml-2 chip text-[10px] bg-amber-50 text-amber-800 border-amber-200">Archiviata</span>}
            {c.requires_coperto && <span className="ml-2 chip text-[10px] bg-sand-100 text-ink-700 border-sand-300">coperto</span>}
            <span className="ml-2 text-xs text-ink-500">#{c.sort_order}</span>
            {activeChildren > 0 && <span className="ml-2 chip text-[10px] bg-bronze-50 text-bronze-700 border-bronze-200">{activeChildren} sottocategorie</span>}
          </div>
          <div className="mt-1 flex flex-col gap-0.5" data-testid={`cat-slots-preview-${c.id}`}>
            {slotsPreview(c.time_slots, c.available_from, c.available_to, c.days).map((line, idx) => (
              <div key={idx} className="text-xs text-ink-500 flex items-center gap-1.5">
                {line.icon ? <span className="text-[13px] leading-none">{line.icon}</span> : <Clock className="w-3 h-3" />}
                <span>{line.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 mt-0.5">
          <span className="chip whitespace-nowrap">{c.visible ? "visibile" : "nascosta"}</span>
          <CategoryDialog category={c} onSaved={onReload} allCategories={allCats} trigger={
            <button data-testid={`edit-cat-${c.id}`} className="p-1.5 rounded-full hover:bg-sand-200"><Pencil className="w-4 h-4" /></button>
          } />
          <button onClick={() => onDuplicate(c.id)} data-testid={`dup-cat-${c.id}`} className="p-1.5 rounded-full hover:bg-sand-200" title="Duplica categoria"><Copy className="w-4 h-4" /></button>
          <button onClick={() => onArchive(c)} data-testid={`arch-cat-${c.id}`} className="p-1.5 rounded-full hover:bg-sand-200">
            {c.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
          </button>
          <button onClick={() => onDelete(c.id)} data-testid={`del-cat-${c.id}`} className="p-1.5 rounded-full text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Sub-categories droppable area (also target for cross-container drop when collapsed). */}
      <div ref={setDropRef} className={`px-3 pb-2 ${isExpanded ? "" : "min-h-0"}`}>
        <div style={{
          maxHeight: isExpanded ? "9999px" : "0px",
          opacity: isExpanded ? 1 : 0,
          overflow: "hidden",
          transition: "max-height 250ms ease, opacity 200ms ease",
        }}>
          <div className="border-l-2 border-bronze-200 pl-3 ml-2">
            <SortableContext items={childCats.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="grid gap-1.5" data-testid={`cat-children-${c.id}`}>
                {childCats.map((s) => (
                  <SubCategoryRow key={s.id} c={s} allCats={allCats}
                                   onReload={onReload}
                                   onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete}
                                   onPromote={() => onSetParent(s.id, null)} />
                ))}
                {childCats.length === 0 && (
                  <div className="text-xs text-ink-500 italic py-1.5">
                    Nessuna sottocategoria. Trascina qui una categoria per annidarla.
                  </div>
                )}
              </div>
            </SortableContext>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubCategoryRow({ c, allCats, onReload, onDuplicate, onArchive, onDelete, onPromote }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`cat-row-${c.id}`}
         className={`flex items-start gap-2 rounded-lg border border-sand-300 bg-sand-50/60 px-3 py-1.5 text-sm ${c.archived ? "opacity-50" : ""}`}>
      <button {...attributes} {...listeners} data-testid={`drag-cat-${c.id}`}
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-sand-200 touch-none mt-0.5">
        <GripVertical className="w-4 h-4 text-ink-500" />
      </button>
      <div className="min-w-0 flex-1">
        <div><span className="text-bronze-700 mr-1">↳</span><b>{c.name}</b> <span className="text-ink-500">· {c.department}</span>
          {c.archived && <span className="ml-2 chip text-[10px] bg-amber-50 text-amber-800 border-amber-200">Archiviata</span>}
          <span className="ml-2 text-xs text-ink-500">#{c.sort_order}</span>
        </div>
        <div className="mt-1 flex flex-col gap-0.5" data-testid={`cat-slots-preview-${c.id}`}>
          {slotsPreview(c.time_slots, c.available_from, c.available_to, c.days).map((line, idx) => (
            <div key={idx} className="text-[11px] text-ink-500 flex items-center gap-1.5">
              {line.icon ? <span className="leading-none">{line.icon}</span> : <Clock className="w-3 h-3" />}
              <span>{line.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0 mt-0.5">
        <span className="chip text-[10px] whitespace-nowrap">{c.visible ? "visibile" : "nascosta"}</span>
        <button onClick={onPromote} data-testid={`promote-cat-${c.id}`} title="Promuovi a categoria principale"
                className="p-1.5 rounded-full hover:bg-sand-200 text-bronze-700">
          <ChevronRight className="w-4 h-4 rotate-[-90deg]" />
        </button>
        <CategoryDialog category={c} onSaved={onReload} allCategories={allCats} trigger={
          <button data-testid={`edit-cat-${c.id}`} className="p-1.5 rounded-full hover:bg-sand-200"><Pencil className="w-4 h-4" /></button>
        } />
        <button onClick={() => onDuplicate(c.id)} data-testid={`dup-cat-${c.id}`} className="p-1.5 rounded-full hover:bg-sand-200" title="Duplica"><Copy className="w-4 h-4" /></button>
        <button onClick={() => onArchive(c)} data-testid={`arch-cat-${c.id}`} className="p-1.5 rounded-full hover:bg-sand-200">
          {c.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
        </button>
        <button onClick={() => onDelete(c.id)} data-testid={`del-cat-${c.id}`} className="p-1.5 rounded-full text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PRODUCTS DRAG-DROP BY CATEGORY

const EXPANDED_KEY = "sarabi:menu:expanded_categories";
function loadExpanded() {
  try {
    const raw = sessionStorage.getItem(EXPANDED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function saveExpanded(state) {
  try { sessionStorage.setItem(EXPANDED_KEY, JSON.stringify(state || {})); } catch {}
}
function highlightText(text, term) {
  const t = (text ?? "").toString();
  if (!term) return t;
  try {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(${escaped})`, "ig");
    const parts = t.split(re);
    return parts.map((part, i) =>
      re.test(part)
        ? <mark key={i} className="bg-yellow-200 rounded-sm px-0.5">{part}</mark>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  } catch { return t; }
}

function ProductsByCategory({ cats, prods, mods, onReload, onToggleAvailability, onDuplicate, onArchive, onDelete, duplicateCat, archiveCat, deleteCat }) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(loadExpanded);
  const [savedExpanded, setSavedExpanded] = useState(null);

  const grouped = useMemo(() => {
    const g = {};
    cats.forEach((c) => { g[c.id] = { cat: c, items: [] }; });
    prods.forEach((p) => { if (g[p.category_id]) g[p.category_id].items.push(p); });
    Object.values(g).forEach((b) => b.items.sort((a, b2) => (a.sort_order || 0) - (b2.sort_order || 0)));
    return g;
  }, [cats, prods]);

  const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const q = search.trim();
  const nq = norm(q);

  const filteredGrouped = useMemo(() => {
    if (!nq) return grouped;
    const out = {};
    Object.entries(grouped).forEach(([cid, b]) => {
      const catMatch = norm(b.cat.name).includes(nq);
      const items = b.items.filter((p) => norm(p.name).includes(nq) || norm(p.description || "").includes(nq));
      if (catMatch || items.length > 0) {
        out[cid] = { cat: b.cat, items: catMatch && items.length === 0 ? b.items : items };
      }
    });
    return out;
  }, [grouped, nq]);

  useEffect(() => {
    if (nq && savedExpanded === null) {
      setSavedExpanded({ ...expanded });
      const nextExpanded = { ...expanded };
      Object.keys(filteredGrouped).forEach((cid) => { nextExpanded[cid] = true; });
      setExpanded(nextExpanded);
    } else if (!nq && savedExpanded !== null) {
      setExpanded(savedExpanded);
      setSavedExpanded(null);
    }
    // eslint-disable-next-line
  }, [nq]);

  useEffect(() => { saveExpanded(expanded); }, [expanded]);

  const toggleCat = (cid) => setExpanded((s) => ({ ...s, [cid]: !s[cid] }));
  const expandAll = () => { const m = {}; cats.forEach((c) => { m[c.id] = true; }); setExpanded(m); };
  const collapseAll = () => setExpanded({});

  const bulkAvailability = async (cid, available) => {
    if (!window.confirm(`Confermi di rendere ${available ? "DISPONIBILI" : "ESAURITI"} tutti i prodotti di questa categoria?`)) return;
    try {
      await api.post("/admin/products/bulk-availability", { category_id: cid, available });
      toast.success(`Prodotti aggiornati (${available ? "disponibili" : "esauriti"})`);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visibleCats = useMemo(() => {
    const roots = cats.filter((c) => !c.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    if (!nq) return roots;
    // In search mode: keep roots that either match or contain a matching subcategory.
    return roots.filter((r) => {
      if (filteredGrouped[r.id]) return true;
      return cats.some((s) => s.parent_id === r.id && filteredGrouped[s.id]);
    });
  }, [cats, nq, filteredGrouped]);
  const childCatsOf = (rid) => cats
    .filter((c) => c.parent_id === rid && (!nq || filteredGrouped[c.id]))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none" />
          <Input data-testid="menu-search" value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Cerca prodotto o categoria… (auto-espande i risultati)"
                 className="pl-9 pr-9 rounded-full" />
          {search && (
            <button data-testid="menu-search-clear" onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-sand-200 flex items-center justify-center">
              <ClearIcon className="w-4 h-4" />
            </button>
          )}
        </div>
        <button onClick={expandAll} className="text-xs rounded-full border border-sand-300 px-3 py-1 hover:bg-sand-100" data-testid="menu-expand-all">Espandi tutto</button>
        <button onClick={collapseAll} className="text-xs rounded-full border border-sand-300 px-3 py-1 hover:bg-sand-100" data-testid="menu-collapse-all">Chiudi tutto</button>
      </div>

      <div className="space-y-3">
        {visibleCats.map((c) => (
          <CategoryProductsBlock key={c.id} category={c} products={filteredGrouped[c.id]?.items || grouped[c.id]?.items || []}
                                  allCategories={cats} allMods={mods}
                                  isExpanded={!!expanded[c.id]}
                                  onToggleExpanded={() => toggleCat(c.id)}
                                  searchTerm={q}
                                  onReload={onReload}
                                  onToggleAvailability={onToggleAvailability}
                                  onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete}
                                  onExpandAll={expandAll} onCollapseAll={collapseAll}
                                  onBulkAvailability={bulkAvailability}
                                  onDuplicateCat={duplicateCat} onArchiveCat={archiveCat} onDeleteCat={deleteCat}
                                  subCategories={childCatsOf(c.id)}
                                  productsByCat={filteredGrouped}
                                  productsByCatFallback={grouped}
                                  expandedMap={expanded} toggleCat={toggleCat} />
        ))}
        {visibleCats.length === 0 && (
          <div className="card-elevated p-6 text-center text-ink-500">Nessuna categoria/prodotto trovato per "{search}"</div>
        )}
      </div>
    </div>
  );
}

function CategoryProductsBlock({ category, products, allCategories, allMods, isExpanded, onToggleExpanded, searchTerm,
                                 onReload, onToggleAvailability, onDuplicate, onArchive, onDelete,
                                 onExpandAll, onCollapseAll, onBulkAvailability,
                                 onDuplicateCat, onArchiveCat, onDeleteCat,
                                 subCategories, productsByCat, productsByCatFallback, expandedMap, toggleCat }) {
  const [items, setItems] = useState(products);
  useEffect(() => { setItems(products); }, [products]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
                             useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const newArr = arrayMove(items, oldIdx, newIdx);
    setItems(newArr);
    try {
      await api.post("/admin/products/reorder", { items: newArr.map((p, idx) => ({ id: p.id, sort_order: idx * 10 })) });
      toast.success("Ordine salvato");
    } catch (err) { toast.error(formatApiError(err)); onReload(); }
  };

  const total = items.length;
  const avail = items.filter((p) => p.available).length;
  const unavail = total - avail;
  const subs = subCategories || [];

  return (
    <div className="rounded-2xl border border-sand-300 overflow-hidden" data-testid={`cat-block-${category.id}`}>
      <div className="px-3 py-2 bg-sand-50 border-b border-sand-300 flex items-center gap-2">
        <button onClick={onToggleExpanded} data-testid={`cat-toggle-${category.id}`}
                className="flex items-center gap-2 flex-1 min-w-0 text-left group">
          <ChevronRight className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
          <span className="font-serif text-lg truncate">
            {searchTerm ? highlightText(category.name, searchTerm) : category.name}
          </span>
          <span className="text-xs text-ink-500 shrink-0">
            {total} prodotti · <span className="text-emerald-700">{avail} disponibili</span> · <span className="text-red-700">{unavail} esauriti</span>
            {subs.length > 0 && <> · <span className="text-bronze-700">{subs.length} sottocategorie</span></>}
          </span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`cat-actions-${category.id}`} className="p-1.5 rounded-full hover:bg-sand-200" aria-label="Comandi rapidi">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExpandAll} data-testid={`menu-expand-all-${category.id}`}>Espandi tutto</DropdownMenuItem>
            <DropdownMenuItem onClick={onCollapseAll} data-testid={`menu-collapse-all-${category.id}`}>Chiudi tutto</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onBulkAvailability(category.id, true)} data-testid={`menu-bulk-on-${category.id}`}>Rendi disponibili tutti</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onBulkAvailability(category.id, false)} data-testid={`menu-bulk-off-${category.id}`}>Esaurisci tutti</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDuplicateCat && onDuplicateCat(category.id)} data-testid={`menu-dup-cat-${category.id}`}>Duplica categoria</DropdownMenuItem>
            <CategoryDialog category={category} onSaved={onReload} allCategories={allCategories} trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`menu-edit-cat-${category.id}`}>Modifica categoria</DropdownMenuItem>
            } />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div style={{
        maxHeight: isExpanded ? "9999px" : "0px",
        opacity: isExpanded ? 1 : 0,
        overflow: "hidden",
        transition: "max-height 250ms ease, opacity 200ms ease",
      }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="divide-y divide-sand-200">
              {items.length === 0 && subs.length === 0 && (
                <div className="px-4 py-6 text-center text-sm text-ink-500">Nessun prodotto in questa categoria</div>
              )}
              {items.length === 0 && subs.length > 0 && (
                <div className="px-4 py-2 text-xs text-ink-500 italic">Nessun prodotto diretto in "{category.name}". Vedi sottocategorie sotto.</div>
              )}
              {items.map((p) => (
                <SortableProductRow key={p.id} p={p} allCategories={allCategories} allMods={allMods}
                                     searchTerm={searchTerm}
                                     onReload={onReload}
                                     onToggleAvailability={onToggleAvailability}
                                     onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {subs.length > 0 && (
          <div className="p-2 space-y-2 bg-white/50">
            {subs.map((sc) => {
              const scProducts = productsByCat?.[sc.id]?.items || productsByCatFallback?.[sc.id]?.items || [];
              const scExpanded = !!expandedMap?.[sc.id];
              return (
                <SubCategoryProductsBlock key={sc.id} category={sc}
                                           products={scProducts}
                                           allCategories={allCategories} allMods={allMods}
                                           isExpanded={scExpanded}
                                           onToggleExpanded={() => toggleCat && toggleCat(sc.id)}
                                           searchTerm={searchTerm}
                                           onReload={onReload}
                                           onToggleAvailability={onToggleAvailability}
                                           onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete}
                                           onDuplicateCat={onDuplicateCat} onArchiveCat={onArchiveCat} onDeleteCat={onDeleteCat} />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SubCategoryProductsBlock({ category, products, allCategories, allMods, isExpanded, onToggleExpanded, searchTerm,
                                    onReload, onToggleAvailability, onDuplicate, onArchive, onDelete,
                                    onDuplicateCat, onArchiveCat, onDeleteCat }) {
  const [items, setItems] = useState(products);
  useEffect(() => { setItems(products); }, [products]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
                             useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const newArr = arrayMove(items, oldIdx, newIdx);
    setItems(newArr);
    try {
      await api.post("/admin/products/reorder", { items: newArr.map((p, idx) => ({ id: p.id, sort_order: idx * 10 })) });
      toast.success("Ordine salvato");
    } catch (err) { toast.error(formatApiError(err)); onReload(); }
  };
  const total = items.length;
  const avail = items.filter((p) => p.available).length;
  const unavail = total - avail;

  return (
    <div className="rounded-xl border border-sand-200 bg-sand-50/60 overflow-hidden ml-4" data-testid={`cat-block-${category.id}`}>
      <div className="px-3 py-1.5 bg-white/80 border-b border-sand-200 flex items-center gap-2">
        <button onClick={onToggleExpanded} data-testid={`cat-toggle-${category.id}`}
                className="flex items-center gap-2 flex-1 min-w-0 text-left group">
          <ChevronRight className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
          <span className="text-sm truncate"><span className="text-bronze-700 mr-1">↳</span>
            {searchTerm ? highlightText(category.name, searchTerm) : category.name}
          </span>
          <span className="text-xs text-ink-500 shrink-0">
            {total} prodotti · <span className="text-emerald-700">{avail}</span> · <span className="text-red-700">{unavail}</span>
          </span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`cat-actions-${category.id}`} className="p-1 rounded-full hover:bg-sand-200" aria-label="Comandi">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onDuplicateCat && onDuplicateCat(category.id)}>Duplica sottocategoria</DropdownMenuItem>
            <CategoryDialog category={category} onSaved={onReload} allCategories={allCategories} trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`menu-edit-cat-${category.id}`}>Modifica sottocategoria</DropdownMenuItem>
            } />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div style={{
        maxHeight: isExpanded ? "9999px" : "0px",
        opacity: isExpanded ? 1 : 0,
        overflow: "hidden",
        transition: "max-height 250ms ease, opacity 200ms ease",
      }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="divide-y divide-sand-200">
              {items.length === 0 && (
                <div className="px-4 py-3 text-center text-xs text-ink-500">Nessun prodotto in questa sottocategoria</div>
              )}
              {items.map((p) => (
                <SortableProductRow key={p.id} p={p} allCategories={allCategories} allMods={allMods}
                                     searchTerm={searchTerm}
                                     onReload={onReload}
                                     onToggleAvailability={onToggleAvailability}
                                     onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

function LegacyCategoryProductsBlockUnused({ category, products, allCategories, allMods, onReload, onToggleAvailability, onDuplicate, onArchive, onDelete }) {
  const [items, setItems] = useState(products);
  useEffect(() => { setItems(products); }, [products]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
                             useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const newArr = arrayMove(items, oldIdx, newIdx);
    setItems(newArr);
    const payload = { items: newArr.map((p, idx) => ({ id: p.id, sort_order: idx * 10 })) };
    try {
      await api.post("/admin/products/reorder", payload);
      toast.success("Ordine salvato");
    } catch (err) { toast.error(formatApiError(err)); onReload(); }
  };

  if (items.length === 0) return null;
  return (
    <div className="rounded-2xl border border-sand-300" data-testid={`cat-block-${category.id}`}>
      <div className="px-4 py-2 bg-sand-50 border-b border-sand-300 rounded-t-2xl">
        <span className="font-serif text-lg">{category.name}</span>
        <span className="text-xs text-ink-500 ml-2">{items.length} prodotti · {category.department}</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="divide-y divide-sand-200">
            {items.map((p) => (
              <SortableProductRow key={p.id} p={p} allCategories={allCategories} allMods={allMods}
                                   onReload={onReload}
                                   onToggleAvailability={onToggleAvailability}
                                   onDuplicate={onDuplicate} onArchive={onArchive} onDelete={onDelete} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function SortableProductRow({ p, allCategories, allMods, onReload, onToggleAvailability, onDuplicate, onArchive, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`prod-row-${p.id}`}
         className={`flex items-center gap-3 px-3 py-2 text-sm hover:bg-sand-50/50 ${p.archived ? "opacity-50" : ""}`}>
      <button {...attributes} {...listeners} data-testid={`drag-prod-${p.id}`}
              className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-sand-200 touch-none">
        <GripVertical className="w-4 h-4 text-ink-500" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{p.name}
          {p.modifier_group_ids?.length ? <span className="ml-2 text-xs text-bronze-600">· {p.modifier_group_ids.length} modificatori</span> : null}
        </div>
        <div className="text-xs text-ink-500">{eur(p.price_cents)} · {p.department || "-"} · #{p.sort_order}</div>
      </div>
      <Switch checked={p.available} onCheckedChange={() => onToggleAvailability(p)} data-testid={`avail-${p.id}`} />
      <ProductDialog product={p} categories={allCategories} modifiers={allMods} onSaved={onReload} trigger={
        <button data-testid={`edit-prod-${p.id}`} className="p-1.5 rounded-full hover:bg-sand-200"><Pencil className="w-4 h-4" /></button>
      } />
      <button onClick={() => onDuplicate(p.id)} data-testid={`dup-prod-${p.id}`} className="p-1.5 rounded-full hover:bg-sand-200"><Copy className="w-4 h-4" /></button>
      <button onClick={() => onArchive(p)} data-testid={`arch-prod-${p.id}`} className="p-1.5 rounded-full hover:bg-sand-200">
        {p.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
      </button>
      <button onClick={() => onDelete(p.id)} className="p-1.5 rounded-full text-red-700 hover:bg-red-50" data-testid={`del-prod-${p.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MODIFIER GROUPS FULL MANAGEMENT

function ModifierGroupsSection({ groups, onReload }) {
  const [items, setItems] = useState(groups);
  useEffect(() => { setItems(groups); }, [groups]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
                             useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const newArr = arrayMove(items, oldIdx, newIdx);
    setItems(newArr);
    try {
      await api.post("/admin/modifier-groups/reorder", { items: newArr.map((g, i) => ({ id: g.id, sort_order: i * 10 })) });
      toast.success("Ordine gruppi salvato");
    } catch (err) { toast.error(formatApiError(err)); onReload(); }
  };

  return (
    <section className="card-elevated p-5 mt-6">
      <div className="flex justify-between items-center">
        <div className="eyebrow">Gruppi modificatori ({items.length})</div>
        <ModifierGroupDialog onSaved={onReload} />
      </div>
      <div className="text-xs text-ink-500 mt-1">Trascina per riordinare. I gruppi appariranno nell'ordine impostato nel menu cliente.</div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="mt-4 grid gap-2">
            {items.map((g) => <SortableGroupRow key={g.id} group={g} onReload={onReload} />)}
            {items.length === 0 && <div className="text-center py-8 text-ink-500">Nessun gruppo. Creane uno con "+ Nuovo gruppo".</div>}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
}

function SortableGroupRow({ group, onReload }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const [showUsage, setShowUsage] = useState(false);
  const [usage, setUsage] = useState([]);

  const duplicate = async () => { try { await api.post(`/admin/modifier-groups/${group.id}/duplicate`); onReload(); toast.success("Gruppo duplicato"); } catch (e) { toast.error(formatApiError(e)); } };
  const archive = async () => { try { await api.post(`/admin/modifier-groups/${group.id}/${group.archived ? "unarchive" : "archive"}`); onReload(); } catch (e) { toast.error(formatApiError(e)); } };
  const remove = async () => {
    if (!window.confirm(`Eliminare/archiviare "${group.name}"? Se usato da prodotti, verrà solo archiviato.`)) return;
    try { const r = await api.delete(`/admin/modifier-groups/${group.id}`); onReload(); toast.success(r.data.archived ? "Archiviato (in uso)" : "Eliminato"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadUsage = async () => {
    setShowUsage(!showUsage);
    if (!showUsage && usage.length === 0) {
      try { setUsage((await api.get(`/admin/modifier-groups/${group.id}/usage`)).data); } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`mgroup-${group.id}`}
         className={`rounded-xl border border-sand-300 p-3 bg-white ${group.archived ? "opacity-50" : ""}`}>
      <div className="flex items-start gap-3">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-sand-200 touch-none mt-1"
                data-testid={`drag-mgroup-${group.id}`}><GripVertical className="w-4 h-4 text-ink-500" /></button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{group.name}</span>
            {group.archived && <span className="chip text-[10px] bg-amber-50 text-amber-800 border-amber-200">Archiviato</span>}
            {!group.active && !group.archived && <span className="chip text-[10px]">Inattivo</span>}
            <span className="chip text-[11px]">{group.required ? "obbligatorio" : "facoltativo"}</span>
            <span className="chip text-[11px]">{group.multi ? `multi ${group.min_select}-${group.max_select}` : "singola"}</span>
            <button onClick={loadUsage} className="chip text-[11px] hover:bg-sand-200" data-testid={`usage-${group.id}`}>
              <Package className="w-3 h-3 inline mr-1" />
              {group.usage_count} prodotti
              {showUsage ? <ChevronDown className="w-3 h-3 inline ml-0.5" /> : <ChevronRight className="w-3 h-3 inline ml-0.5" />}
            </button>
          </div>
          {group.description && <div className="text-xs text-ink-500 mt-1">{group.description}</div>}
          <div className="text-xs text-ink-500 mt-1">
            {(group.options || []).map((o) => `${o.name}${o.price_delta_cents ? ` (+${(o.price_delta_cents / 100).toFixed(2)}€)` : ""}`).join(" · ")}
          </div>
          {showUsage && (
            <div className="mt-2 text-xs bg-sand-50 rounded-lg p-2">
              {usage.length === 0 ? "Nessun prodotto usa questo gruppo." :
                <ul className="list-disc list-inside">{usage.map((u) => <li key={u.id}>{u.name}</li>)}</ul>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <ModifierGroupDialog group={group} onSaved={onReload} trigger={
            <button data-testid={`edit-mgroup-${group.id}`} className="p-1.5 rounded-full hover:bg-sand-200"><Pencil className="w-4 h-4" /></button>
          } />
          <button onClick={duplicate} data-testid={`dup-mgroup-${group.id}`} className="p-1.5 rounded-full hover:bg-sand-200"><Copy className="w-4 h-4" /></button>
          <button onClick={archive} data-testid={`arch-mgroup-${group.id}`} className="p-1.5 rounded-full hover:bg-sand-200">
            {group.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
          </button>
          <button onClick={remove} data-testid={`del-mgroup-${group.id}`} className="p-1.5 rounded-full text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

const emptyGroup = () => ({
  name: "", description: "", customer_description: "",
  active: true, required: false, multi: false, min_select: 0, max_select: 1,
  sort_order: 0, options: [],
});

// Phase 14 — sortable row for one option inside the group dialog. Drag handle
// only; the rest keeps the pre-existing inline editing UX (name, desc, price,
// active/default switches, duplicate/delete). Extended with the new `badges`
// pill editor (comma-separated free labels).
function SortableOptionRow({ id, index, option, setOpt, rmOpt, dupOpt }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const badgesText = (option.badges || []).join(", ");
  const setBadges = (raw) => setOpt(index, {
    badges: raw.split(",").map((s) => s.trim()).filter(Boolean),
  });
  return (
    <div ref={setNodeRef} style={style}
         className="rounded-xl border border-sand-300 p-3 bg-sand-50/50 grid grid-cols-12 gap-2 items-end"
         data-testid={`mg-opt-${index}`}>
      <button type="button" {...attributes} {...listeners}
              className="col-span-1 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-sand-200 touch-none self-center"
              data-testid={`mg-opt-drag-${index}`} aria-label="Trascina opzione">
        <GripVertical className="w-4 h-4 text-ink-500 mx-auto" />
      </button>
      <div className="col-span-4">
        <Label className="text-xs">Nome</Label>
        <Input value={option.name}
               onChange={(e) => setOpt(index, { name: e.target.value })}
               data-testid={`mg-opt-name-${index}`} />
      </div>
      <div className="col-span-3">
        <Label className="text-xs">Badge (separati da ,)</Label>
        <Input value={badgesText}
               placeholder="es. bio, consigliato"
               onChange={(e) => setBadges(e.target.value)}
               data-testid={`mg-opt-badges-${index}`} />
      </div>
      <div className="col-span-2">
        <Label className="text-xs">Δ prezzo (€)</Label>
        <Input type="number" step="0.10" value={((option.price_delta_cents || 0) / 100).toFixed(2)}
               onChange={(e) => setOpt(index, { price_delta_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
               data-testid={`mg-opt-price-${index}`} />
      </div>
      <div className="col-span-1 text-center">
        <Label className="text-xs">Attiva</Label>
        <div className="pt-1.5">
          <Switch checked={option.active !== false}
                  onCheckedChange={(v) => setOpt(index, { active: v })}
                  data-testid={`mg-opt-active-${index}`} />
        </div>
      </div>
      <div className="col-span-1 flex gap-0.5 justify-end">
        <button type="button" onClick={() => dupOpt(index)}
                className="p-1 hover:bg-sand-200 rounded"
                data-testid={`mg-opt-dup-${index}`}>
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => rmOpt(index)}
                className="p-1 hover:bg-red-50 text-red-700 rounded"
                data-testid={`mg-opt-del-${index}`}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="col-span-12 grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Descrizione</Label>
          <Input value={option.description || ""}
                 onChange={(e) => setOpt(index, { description: e.target.value })}
                 data-testid={`mg-opt-desc-${index}`} />
        </div>
        <label className="flex items-center gap-2 text-xs self-end pb-1.5">
          <Switch checked={!!option.default_selected}
                  onCheckedChange={(v) => setOpt(index, { default_selected: v })}
                  data-testid={`mg-opt-default-${index}`} />
          Preselezionata
        </label>
      </div>
    </div>
  );
}

function ModifierGroupDialog({ group, onSaved, trigger }) {
  const isEdit = !!group;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyGroup());

  useEffect(() => {
    if (!open) return;
    setF(isEdit ? { ...emptyGroup(), ...group, options: (group.options || []).map((o) => ({ ...o })) } : emptyGroup());
  }, [open, isEdit, group]);

  const addOpt = () => setF({ ...f, options: [...f.options, { name: "", price_delta_cents: 0, sort_order: f.options.length, active: true, default_selected: false, badges: [] }] });
  const setOpt = (i, patch) => setF({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, ...patch } : o) });
  const rmOpt = (i) => setF({ ...f, options: f.options.filter((_, idx) => idx !== i) });
  const dupOpt = (i) => { const o = f.options[i]; setF({ ...f, options: [...f.options.slice(0, i + 1), { ...o, name: `${o.name} (Copia)`, badges: [...(o.badges || [])] }, ...f.options.slice(i + 1)] }); };
  const moveOpt = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= f.options.length || to >= f.options.length) return;
    const next = [...f.options];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setF({ ...f, options: next });
  };

  const optSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
                                useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onOptDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = f.options.findIndex((_, i) => `opt-${i}` === active.id);
    const to   = f.options.findIndex((_, i) => `opt-${i}` === over.id);
    moveOpt(from, to);
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Nome obbligatorio");
    if (f.options.length === 0) return toast.error("Aggiungi almeno un'opzione");
    if (f.options.some((o) => !o.name.trim())) return toast.error("Nome opzione obbligatorio");
    const payload = { ...f, options: f.options.map((o, i) => ({
      ...o, sort_order: i,
      price_delta_cents: parseInt(o.price_delta_cents) || 0,
      badges: Array.isArray(o.badges) ? o.badges.filter(Boolean) : [],
    })) };
    try {
      if (isEdit) await api.patch(`/admin/modifier-groups/${group.id}`, payload);
      else await api.post("/admin/modifier-groups", payload);
      setOpen(false); onSaved();
      toast.success(isEdit ? "Gruppo aggiornato" : "Gruppo creato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button data-testid="new-mgroup-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Nuovo gruppo</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? `Modifica gruppo ${group.name}` : "Nuovo gruppo modificatori"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mg-name" /></div>
            <div><Label>Ordine</Label><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })} data-testid="mg-sort" /></div>
          </div>
          <div><Label>Descrizione interna</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="mg-desc" /></div>
          <div><Label>Descrizione cliente</Label><Textarea value={f.customer_description || ""} onChange={(e) => setF({ ...f, customer_description: e.target.value })} data-testid="mg-cust-desc" /></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="mg-active" /> Attivo</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.required} onCheckedChange={(v) => setF({ ...f, required: v })} data-testid="mg-required" /> Obbligatorio</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.multi} onCheckedChange={(v) => setF({ ...f, multi: v, min_select: v ? f.min_select : 0, max_select: v ? Math.max(1, f.max_select) : 1 })} data-testid="mg-multi" /> Selezione multipla</label>
          </div>
          {f.multi && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Min. selezioni</Label><Input type="number" min="0" value={f.min_select} onChange={(e) => setF({ ...f, min_select: parseInt(e.target.value) || 0 })} data-testid="mg-min" /></div>
              <div><Label>Max. selezioni</Label><Input type="number" min="1" value={f.max_select} onChange={(e) => setF({ ...f, max_select: parseInt(e.target.value) || 1 })} data-testid="mg-max" /></div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <Label>Opzioni ({f.options.length})</Label>
              <Button type="button" size="sm" variant="outline" onClick={addOpt} data-testid="mg-add-opt">
                <Plus className="w-3 h-3 mr-1" />Aggiungi opzione
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              <DndContext sensors={optSensors} collisionDetection={closestCenter} onDragEnd={onOptDragEnd}>
                <SortableContext items={f.options.map((_, i) => `opt-${i}`)} strategy={verticalListSortingStrategy}>
                  {f.options.map((o, i) => (
                    <SortableOptionRow key={`opt-${i}`} id={`opt-${i}`} index={i} option={o}
                                       setOpt={setOpt} rmOpt={rmOpt} dupOpt={dupOpt} />
                  ))}
                </SortableContext>
              </DndContext>
              {f.options.length === 0 && <div className="text-xs text-ink-500 py-4 text-center border border-dashed border-sand-300 rounded-xl">Nessuna opzione. Clicca "Aggiungi opzione".</div>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} data-testid="mg-save">{isEdit ? "Salva modifiche" : "Crea gruppo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------

function TimeSlotFields({ value, onChange, testidPrefix }) {
  // Legacy wrapper: adapts single-slot value to the new multi-slot editor.
  const asSlots = value.time_slots && value.time_slots.length
    ? value.time_slots
    : ((value.available_from || value.available_to || (value.days && value.days.length))
       ? [{ from_time: value.available_from, to_time: value.available_to, days: value.days || [] }]
       : []);
  const setSlots = (next) => onChange({
    ...value,
    time_slots: next,
    available_from: null, available_to: null, days: null,
  });
  return <MultiTimeSlotFields slots={asSlots} onChange={setSlots} testidPrefix={testidPrefix} />;
}

// -----------------------------------------------------------------------------

const emptyCategory = { name: "", slug: null, department: "bar", sort_order: 100, visible: true, requires_coperto: false, available_from: null, available_to: null, days: [], time_slots: [], available_from_at: null, unavailable_until_at: null, parent_id: null, hierarchical_customer_nav: false, display_type: "default" };

function CategoryDialog({ category, onSaved, trigger, allCategories }) {
  const isEdit = !!category;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyCategory);

  useEffect(() => {
    if (!open) return;
    setF(isEdit ? {
      name: category.name || "", slug: category.slug || null,
      department: category.department || "bar", sort_order: category.sort_order || 100,
      visible: category.visible !== false, requires_coperto: !!category.requires_coperto,
      available_from: category.available_from || null, available_to: category.available_to || null,
      days: category.days || [],
      time_slots: category.time_slots || [],
      available_from_at: category.available_from_at || null,
      unavailable_until_at: category.unavailable_until_at || null,
      parent_id: category.parent_id || null,
      hierarchical_customer_nav: !!category.hierarchical_customer_nav,
      display_type: category.display_type || "default",
    } : emptyCategory);
  }, [open, isEdit, category]);

  // Compute valid parent options: only root categories (parent_id === null),
  // excluding the category itself and any category that already has children
  // (would create 2-level nesting) — but we only need to exclude self here.
  // A category with sub-categories cannot become a child; hide it as parent
  // choice only if the current category IS root (allowed to become child) but
  // ALSO has children (double-nesting risk).
  const cats = allCategories || [];
  const selfHasChildren = isEdit && cats.some((c) => c.parent_id === category?.id);
  const parentOptions = cats
    .filter((c) => !c.parent_id && (!isEdit || c.id !== category?.id) && !c.archived)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Nome obbligatorio");
    const slots = f.time_slots || [];
    const ov = slotsOverlapError(slots);
    if (ov) return toast.error(`Fasce sovrapposte: #${ov.i + 1} e #${ov.j + 1}. Correggi giorni od orari prima di salvare.`);
    if (f.parent_id && selfHasChildren) {
      return toast.error("Questa categoria ha sottocategorie. Rimuovile prima di renderla figlia.");
    }
    try {
      const payload = {
        ...f,
        days: f.days && f.days.length ? f.days : null,
        time_slots: slots.map(({ _key, ...rest }) => rest),
        parent_id: f.parent_id || null,
      };
      if (isEdit) await api.patch(`/admin/categories/${category.id}`, payload);
      else await api.post("/admin/categories", payload);
      setOpen(false); onSaved();
      toast.success(isEdit ? "Categoria aggiornata" : "Categoria creata");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="new-cat-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Nuova categoria</Button>}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? `Modifica ${category.name}` : "Nuova categoria"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cat-name" /></div>

          <div>
            <Label>Categoria principale</Label>
            <Select value={f.parent_id || "__none__"} onValueChange={(v) => setF({ ...f, parent_id: v === "__none__" ? null : v })}>
              <SelectTrigger data-testid="cat-parent"><SelectValue placeholder="Nessuna — categoria principale" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="cat-parent-none">Nessuna — categoria principale</SelectItem>
                {parentOptions.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`cat-parent-opt-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selfHasChildren && (
              <div className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                ⚠ Questa categoria ha sottocategorie: non può diventare figlia finché non le sposti/elimini.
              </div>
            )}
            <p className="text-[11px] text-ink-500 mt-1">
              Solo un livello di nidificazione consentito. Selezionando una categoria padre, questa diventa una <b>sottocategoria</b>.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Slug</Label><Input value={f.slug || ""} onChange={(e) => setF({ ...f, slug: e.target.value })} /></div>
            <div><Label>Ordine</Label><Input type="number" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: parseInt(e.target.value) || 0 })} /></div>
          </div>
          <div><Label>Reparto</Label>
            <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
              <SelectTrigger data-testid="cat-dept"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bar">Bar</SelectItem><SelectItem value="kitchen">Cucina</SelectItem><SelectItem value="beach">Spiaggia</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.visible} onCheckedChange={(v) => setF({ ...f, visible: v })} data-testid="cat-visible" /> Visibile</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.requires_coperto} onCheckedChange={(v) => setF({ ...f, requires_coperto: v })} data-testid="cat-coperto" /> Applica coperto</label>
          </div>

          {!f.parent_id && (
            <div className="rounded-xl border border-sand-300 p-3 bg-bronze-50/30" data-testid="cat-hier-nav-box">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Switch checked={!!f.hierarchical_customer_nav}
                        onCheckedChange={(v) => setF({ ...f, hierarchical_customer_nav: v })}
                        data-testid="cat-hier-nav" />
                <div>
                  <div className="font-medium">Navigazione gerarchica nel menu cliente</div>
                  <div className="text-[11px] text-ink-500 mt-0.5">
                    Se attivo, il cliente vede prima questa categoria come "cartella" e poi entra nelle sue sottocategorie.
                    Utile per menu grandi (es. Food · Bar). Se disattivo, la categoria e le sue sotto sono mostrate in linea come oggi.
                  </div>
                </div>
              </label>
            </div>
          )}

          <div className="rounded-xl border border-sand-300 p-3 bg-sand-50/40" data-testid="cat-display-type-box">
            <Label>Modalità di visualizzazione</Label>
            <Select value={f.display_type || "default"} onValueChange={(v) => setF({ ...f, display_type: v })}>
              <SelectTrigger data-testid="cat-display-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default" data-testid="cat-display-type-default">Default — card prodotto</SelectItem>
                <SelectItem value="wine_list" data-testid="cat-display-type-wine-list">Carta dei vini — elenco elegante + scheda dedicata</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-ink-500 mt-1">
              Le categorie <b>Carta dei vini</b> mostrano una lista in stile ristorante (produttore, nome, denominazione, prezzo).
              Cliccando un vino si apre una <b>scheda dedicata</b>. Utilizzata tipicamente sulle sottocategorie (Bollicine, Bianchi, Rossi…).
            </p>
          </div>

          <TimeSlotFields value={f} onChange={setF} testidPrefix="cat" />

          <div className="rounded-xl border border-sand-300 p-3 bg-sand-50/40" data-testid="cat-schedule">
            <Label className="flex items-center gap-1"><Clock className="w-4 h-4" /> Programmazione categoria</Label>
            <p className="text-[11px] text-ink-500 mt-1">Utile per menu stagionali (Aperitivo, Ferragosto, Natale). La categoria appare/scompare automaticamente.</p>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs">Visibile a partire da</Label>
                <Input type="datetime-local" value={toLocalDatetimeInput(f.available_from_at)}
                       onChange={(e) => setF({ ...f, available_from_at: fromLocalDatetimeInput(e.target.value) })}
                       data-testid="cat-available-from-at" />
              </div>
              <div>
                <Label className="text-xs">Nascondi fino a</Label>
                <Input type="datetime-local" value={toLocalDatetimeInput(f.unavailable_until_at)}
                       onChange={(e) => setF({ ...f, unavailable_until_at: fromLocalDatetimeInput(e.target.value) })}
                       data-testid="cat-unavailable-until-at" />
              </div>
            </div>
            {(f.available_from_at || f.unavailable_until_at) && (
              <button type="button" onClick={() => setF({ ...f, available_from_at: null, unavailable_until_at: null })}
                      className="mt-2 text-xs text-bronze-600 hover:underline" data-testid="cat-schedule-clear">
                Rimuovi programmazione
              </button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} data-testid="cat-save">{isEdit ? "Salva" : "Crea"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------

const EMPTY_WINE = {
  producer: "", country: "", wine_type: "",
  denominazione: "", provenienza: "", tipologia: "", vitigno: "",
  gradazione: "", affinamento: "", colore: "", profumo: "", sapore: "",
  momento_ideale: "", abbinamenti: "", note: "",
  sort_order: 0, featured: false, show_price: true,
  extra: {},
};
const WINE_BADGES = ["Best Seller", "Premium", "Rosé", "Bio", "Consigliato"];

const emptyProduct = () => ({
  name: "", description: "", category_id: "", price_cents: 500, vat_rate: 10, allergens: [], tags: [],
  sku: null, prep_time_min: null, department: "bar",
  available: true, visible: true, sort_order: 100, modifier_group_ids: [],
  image_url: null, image_thumb_url: null,
  available_from: null, available_to: null, days: null, time_slots: [],
  available_from_at: null, unavailable_until_at: null,
  badges: [], wine: null,
});

function toLocalDatetimeInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function fromLocalDatetimeInput(v) {
  if (!v) return null;
  try { return new Date(v).toISOString(); } catch { return null; }
}

function AvailabilityHistorySection({ productId, open }) {
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!open || !productId) return;
    api.get(`/admin/products/${productId}/availability-history?limit=50`)
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [productId, open]);

  const fmtAt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  const srcLabel = (s) => ({ manual: "Manuale", scheduled: "Programmazione automatica", bulk: "Azione massiva", import: "Import" }[s] || s);

  const preview = items ? items.slice(0, 5) : [];
  const rest = items ? items.slice(5) : [];

  return (
    <div className="rounded-xl border border-sand-300 p-3 bg-white" data-testid="prod-history">
      <Label className="flex items-center gap-1"><Clock className="w-4 h-4" /> Storico disponibilità <span className="text-[11px] text-ink-500 font-normal">(solo lettura)</span></Label>
      {items === null && <div className="mt-2 text-xs text-ink-500">Caricamento…</div>}
      {items && items.length === 0 && (
        <div className="mt-2 text-xs text-ink-500 italic">Nessun cambio di stato registrato per questo prodotto.</div>
      )}
      {items && items.length > 0 && (
        <ol className="mt-2 space-y-1.5 border-l-2 border-sand-300 pl-3">
          {(expanded ? items : preview).map((h) => (
            <li key={h.id} className="text-xs" data-testid={`hist-entry-${h.id}`}>
              <div className="text-ink-500">{fmtAt(h.at)}</div>
              <div>
                <b>{h.user_name}</b> <span className="text-[10px] text-ink-500">({h.role})</span>
                <span className="mx-1.5">·</span>
                <span className={h.from_state ? "text-emerald-700" : "text-red-700"}>{h.from_state ? "Disponibile" : "Esaurito"}</span>
                <span className="mx-1 text-ink-500">→</span>
                <span className={h.to_state ? "text-emerald-700" : "text-red-700"}>{h.to_state ? "Disponibile" : "Esaurito"}</span>
                <span className="ml-2 chip text-[10px] bg-sand-100 border-sand-300">{srcLabel(h.source)}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
      {items && rest.length > 0 && (
        <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-2 text-xs text-bronze-600 hover:underline" data-testid="prod-history-toggle">
          {expanded ? "Mostra meno" : `Mostra tutti (+${rest.length})`}
        </button>
      )}
    </div>
  );
}

function ProductDialogPlaceholder() { return null; }

// Phase 11.3 — client-side image picker with auto-generated thumbnail.
//
// Design decisions:
// - No backend upload endpoint, no static mount, no new storage. The file
//   never leaves the browser: FileReader → <canvas> resize → toDataURL('jpeg')
//   produces base64 data URLs that persist in `image_url` /
//   `image_thumb_url` via the existing PATCH /admin/products.
// - Two variants are produced from one file selection: a compact thumb
//   (max 320 px on longest side, JPEG 0.75 ~10-20 KB) for the wine list,
//   and a hi-res full image (max 1400 px on longest side, JPEG 0.85 ~80-150
//   KB) for the wine detail page. Both are entirely optional and default
//   to null — existing products (food, beach, cocktail) are not affected.
// - Existing external URLs (http://... or https://...) still work: we
//   never rewrite `image_url` unless the user picks a new file, so
//   products that already store an external URL keep loading it.
function ProductImagePicker({ imageUrl, thumbUrl, productName, onChange }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);

  const readFile = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error("read fail"));
    fr.readAsDataURL(file);
  });
  const loadImg = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode fail"));
    img.src = src;
  });
  const resize = (img, maxSide, quality) => {
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const cv = document.createElement("canvas");
    cv.width = w; cv.height = h;
    const ctx = cv.getContext("2d");
    // white bg so PNG transparency doesn't turn black in JPEG
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return cv.toDataURL("image/jpeg", quality);
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Formato non supportato. Usa JPG, PNG o WebP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 8 MB).");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await readFile(file);
      const img = await loadImg(dataUrl);
      const full = resize(img, 1400, 0.85);
      const thumb = resize(img, 320, 0.75);
      onChange({ image_url: full, image_thumb_url: thumb });
      toast.success("Immagine caricata. Salva il prodotto per applicarla.");
    } catch (err) {
      toast.error("Impossibile leggere l'immagine.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange({ image_url: null, image_thumb_url: null });

  const preview = thumbUrl || imageUrl;

  return (
    <div className="rounded-xl border border-sand-300 p-3 bg-white" data-testid="product-image-picker">
      <Label className="flex items-center justify-between">
        Immagine prodotto
        <span className="text-[11px] text-ink-500 font-normal">
          thumbnail generata automaticamente
        </span>
      </Label>
      <div className="mt-2 flex items-start gap-3">
        <div className="w-24 h-32 shrink-0 rounded-lg bg-sand-50 border border-sand-300 overflow-hidden flex items-center justify-center" data-testid="product-image-preview">
          {preview ? (
            <img src={preview} alt={productName || "preview"} className="w-full h-full object-contain" />
          ) : (
            <span className="italic text-ink-400 font-serif text-lg">Sarabi</span>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onPick}
            className="hidden"
            data-testid="product-image-file-input"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="text-xs rounded-full border border-sand-300 px-3 py-1.5 bg-white hover:bg-sand-100 disabled:opacity-50"
              data-testid="product-image-upload-btn"
            >
              {busy ? "Elaborazione…" : preview ? "Sostituisci immagine" : "Carica immagine"}
            </button>
            {preview && (
              <button
                type="button"
                disabled={busy}
                onClick={clear}
                className="text-xs rounded-full border border-red-200 px-3 py-1.5 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                data-testid="product-image-remove-btn"
              >
                Elimina immagine
              </button>
            )}
          </div>
          <p className="text-[11px] text-ink-500">
            JPG, PNG o WebP fino a 8 MB. Puoi anche incollare un URL esistente
            nel campo tecnico se preferisci ospitare l'immagine altrove.
          </p>
        </div>
      </div>
    </div>
  );
}

// Phase 11 — wine data + badges editor. Only rendered when the product
// belongs to a category whose display_type is "wine_list". Users can
// pre-fill wine metadata even before switching a category to wine_list
// (fields are hidden from the customer view until the category renderer
// changes), but by default we keep the UI focused: hidden unless needed.
function WineDataFields({ categories, categoryId, wine, badges, onWineChange, onBadgesChange }) {
  const cat = categories.find((c) => c.id === categoryId);
  const isWine = cat && cat.display_type === "wine_list";
  const active = isWine || !!wine;
  if (!active) return null;
  const w = wine || {
    producer: "", country: "", wine_type: "",
    denominazione: "", provenienza: "", tipologia: "", vitigno: "",
    gradazione: "", affinamento: "", colore: "", profumo: "", sapore: "",
    momento_ideale: "", abbinamenti: "", note: "",
    regione: "", zona: "", temperatura_servizio: "", formato: "",
    premi: "", abbinamenti_consigliati: "",
    sort_order: 0, featured: false, show_price: true,
    extra: {},
  };
  const extra = w.extra || {};
  const set = (k, v) => onWineChange({ ...w, [k]: v });
  const setExtra = (next) => onWineChange({ ...w, extra: next });
  const addExtra = () => setExtra({ ...extra, "": "" });
  const updateExtraKey = (oldKey, newKey) => {
    const entries = Object.entries(extra);
    const next = {};
    for (const [k, v] of entries) next[k === oldKey ? newKey : k] = v;
    setExtra(next);
  };
  const updateExtraValue = (key, value) => setExtra({ ...extra, [key]: value });
  const removeExtra = (key) => {
    const next = { ...extra };
    delete next[key];
    setExtra(next);
  };
  const toggleBadge = (b) => {
    const set2 = new Set(badges);
    set2.has(b) ? set2.delete(b) : set2.add(b);
    onBadgesChange(Array.from(set2));
  };
  return (
    <div className="rounded-xl border border-sand-300 p-3 bg-white" data-testid="wine-data-block">
      <Label className="flex items-center gap-2">
        Dati vino
        <span className="text-[11px] text-ink-500 font-normal">(visibili solo se la categoria è "Carta dei vini")</span>
      </Label>

      <div className="mt-3">
        <Label className="text-xs">Badge</Label>
        <div className="mt-1 flex flex-wrap gap-2" data-testid="wine-badges-picker">
          {["Best Seller", "Premium", "Rosé", "Bio", "Consigliato"].map((b) => {
            const on = badges.includes(b);
            return (
              <button type="button" key={b}
                      data-testid={`wine-badge-${b}`}
                      onClick={() => toggleBadge(b)}
                      className={`chip cursor-pointer ${on ? "border-bronze-600 text-bronze-700 bg-bronze-50" : "border-sand-300 text-ink-600"}`}>
                {b}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 items-end" data-testid="wine-display-row">
        <div>
          <Label className="text-xs">Ordine (manuale)</Label>
          <Input type="number" value={Number.isFinite(w.sort_order) ? w.sort_order : 0}
                 onChange={(e) => set("sort_order", parseInt(e.target.value, 10) || 0)}
                 data-testid="wine-field-sort-order" />
          <p className="text-[10px] text-ink-500 mt-0.5">Più basso = più in alto nell'elenco.</p>
        </div>
        <label className="flex items-center gap-2 text-sm rounded-lg border border-sand-300 px-3 py-2 cursor-pointer">
          <Switch checked={!!w.featured} onCheckedChange={(v) => set("featured", v)} data-testid="wine-field-featured" />
          <div>
            <div className="font-medium leading-tight">In evidenza</div>
            <div className="text-[10px] text-ink-500 leading-tight">Bordo accent + ★ senza usare i badge.</div>
          </div>
        </label>
        <label className="flex items-center gap-2 text-sm rounded-lg border border-sand-300 px-3 py-2 cursor-pointer">
          <Switch checked={w.show_price !== false} onCheckedChange={(v) => set("show_price", v)} data-testid="wine-field-show-price" />
          <div>
            <div className="font-medium leading-tight">Mostra prezzo</div>
            <div className="text-[10px] text-ink-500 leading-tight">Off → "Su richiesta", nasconde add-to-cart.</div>
          </div>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        {[
          ["producer", "Produttore"],
          ["country", "Paese"],
          ["wine_type", "Tipo di vino"],
          ["denominazione", "Denominazione"],
          ["regione", "Regione"],
          ["provenienza", "Provenienza / DOC(G)"],
          ["zona", "Zona di produzione"],
          ["tipologia", "Tipologia"],
          ["vitigno", "Vitigno"],
          ["gradazione", "Gradazione"],
          ["temperatura_servizio", "Temperatura di servizio"],
          ["formato", "Formato"],
          ["affinamento", "Affinamento"],
          ["colore", "Colore"],
        ].map(([k, label]) => (
          <div key={k}>
            <Label className="text-xs">{label}</Label>
            <Input value={w[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`wine-field-${k}`} />
          </div>
        ))}
      </div>

      {[
        ["profumo", "Profumo"],
        ["sapore", "Sapore"],
        ["momento_ideale", "Momento ideale"],
        ["abbinamenti", "Abbinamenti"],
        ["abbinamenti_consigliati", "Abbinamenti consigliati (editoriali)"],
        ["premi", "Riconoscimenti / Premi"],
        ["note", "Note degustazione"],
      ].map(([k, label]) => (
        <div key={k} className="mt-2">
          <Label className="text-xs">{label}</Label>
          <Textarea value={w[k] || ""} onChange={(e) => set(k, e.target.value)} rows={2} data-testid={`wine-field-${k}`} />
        </div>
      ))}

      <div className="mt-4 rounded-lg border border-sand-300 p-3 bg-sand-50/60" data-testid="wine-extra-block">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Metadati extra
            <span className="ml-2 text-[10px] text-ink-500 font-normal">
              coppie chiave/valore libere (annata, punteggio, sommelier, …)
            </span>
          </Label>
          <button type="button" onClick={addExtra} data-testid="wine-extra-add"
                  className="text-xs text-bronze-700 hover:underline">+ Aggiungi campo</button>
        </div>
        {Object.entries(extra).length === 0 && (
          <div className="text-[11px] text-ink-500 italic mt-1">Nessun metadato extra.</div>
        )}
        <div className="mt-2 space-y-2">
          {Object.entries(extra).map(([key, value], i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`wine-extra-row-${i}`}>
              <Input placeholder="Chiave (es. annata)" value={key}
                     onChange={(e) => updateExtraKey(key, e.target.value)}
                     data-testid={`wine-extra-key-${i}`} />
              <Input placeholder="Valore" value={value || ""}
                     onChange={(e) => updateExtraValue(key, e.target.value)}
                     data-testid={`wine-extra-value-${i}`} />
              <button type="button" onClick={() => removeExtra(key)}
                      className="text-ink-500 hover:text-red-700"
                      data-testid={`wine-extra-remove-${i}`}>✕</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductDialog({ product, categories, modifiers, onSaved, trigger }) {
  const isEdit = !!product;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(emptyProduct());

  useEffect(() => {
    if (!open) return;
    setF(isEdit ? {
      name: product.name || "", description: product.description || "",
      category_id: product.category_id, price_cents: product.price_cents || 0,
      vat_rate: product.vat_rate || 10, allergens: product.allergens || [], tags: product.tags || [],
      sku: product.sku, prep_time_min: product.prep_time_min, department: product.department || "bar",
      available: product.available !== false, visible: product.visible !== false,
      sort_order: product.sort_order || 100, modifier_group_ids: product.modifier_group_ids || [],
      image_url: product.image_url, image_thumb_url: product.image_thumb_url || null,
      available_from: product.available_from || null,
      available_to: product.available_to || null, days: product.days || null,
      time_slots: product.time_slots || [],
      available_from_at: product.available_from_at || null,
      unavailable_until_at: product.unavailable_until_at || null,
      badges: product.badges || [],
      wine: product.wine ? { ...EMPTY_WINE, ...product.wine } : null,
    } : { ...emptyProduct(), category_id: categories[0]?.id || "" });
  }, [open, isEdit, product, categories]);

  const toggleMod = (mid) => {
    const set = new Set(f.modifier_group_ids);
    set.has(mid) ? set.delete(mid) : set.add(mid);
    setF({ ...f, modifier_group_ids: Array.from(set) });
  };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Nome obbligatorio");
    if (!f.category_id) return toast.error("Seleziona categoria");
    const slots = f.time_slots || [];
    const ov = slotsOverlapError(slots);
    if (ov) return toast.error(`Fasce sovrapposte: #${ov.i + 1} e #${ov.j + 1}. Correggi giorni od orari prima di salvare.`);
    try {
      // Strip an empty wine object down to null so the backend receives a
      // clean payload for non-wine products.
      const wineIsMeaningful = f.wine && (() => {
        const { sort_order, featured, show_price, extra, ...rest } = f.wine;
        const hasTextField = Object.values(rest).some((v) => (v || "").toString().trim());
        const hasExtra = extra && Object.keys(extra).some((k) => (k || "").trim());
        // sort_order/featured/show_price alone don't count as "meaningful":
        // they're display hints and default values shouldn't force wine=null
        // to flip into an object for non-wine products.
        return hasTextField || hasExtra;
      })();
      const wineOut = wineIsMeaningful
        ? Object.fromEntries(Object.entries(f.wine).map(([k, v]) => {
            if (k === "extra") {
              const cleaned = Object.fromEntries(
                Object.entries(v || {}).filter(([kk, vv]) => (kk || "").trim() && (vv || "").toString().trim())
              );
              return [k, cleaned];
            }
            if (k === "sort_order") return [k, parseInt(v, 10) || 0];
            if (k === "featured") return [k, !!v];
            if (k === "show_price") return [k, v !== false];
            return [k, (v || "").toString().trim() || null];
          }))
        : null;
      const payload = {
        ...f,
        time_slots: slots.map(({ _key, ...rest }) => rest),
        available_from: null, available_to: null, days: null,
        badges: (f.badges || []).filter(Boolean),
        wine: wineOut,
      };
      if (isEdit) await api.patch(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      setOpen(false); onSaved();
      toast.success(isEdit ? "Prodotto aggiornato" : "Prodotto creato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button data-testid="new-prod-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Nuovo prodotto</Button>}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? `Modifica ${product.name}` : "Nuovo prodotto"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="prod-name" /></div>
            <div><Label>Categoria</Label>
              <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v })}>
                <SelectTrigger data-testid="prod-cat"><SelectValue placeholder="Seleziona una categoria" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {categories.filter((c) => !c.parent_id && !c.archived)
                    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                    .flatMap((root) => {
                      const subs = categories.filter((c) => c.parent_id === root.id && !c.archived)
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                      return [
                        <SelectItem key={root.id} value={root.id} data-testid={`prod-cat-opt-${root.id}`}>
                          <span className="font-medium">{root.name}</span>
                        </SelectItem>,
                        ...subs.map((s) => (
                          <SelectItem key={s.id} value={s.id} data-testid={`prod-cat-opt-${s.id}`} className="pl-6">
                            <span className="text-bronze-700 mr-1">↳</span>{s.name}
                          </SelectItem>
                        )),
                      ];
                    })}
                </SelectContent>
              </Select>
              {f.category_id && (() => {
                const sel = categories.find((c) => c.id === f.category_id);
                if (!sel) return null;
                const parent = sel.parent_id ? categories.find((c) => c.id === sel.parent_id) : null;
                return (
                  <div className="mt-1 text-[11px] text-bronze-700" data-testid="prod-cat-breadcrumb">
                    {parent ? <>{parent.name} <span className="mx-1">›</span> <b>{sel.name}</b></> : <b>{sel.name}</b>}
                  </div>
                );
              })()}
            </div>
          </div>
          <div><Label>Descrizione</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="prod-desc" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Prezzo (€)</Label><Input type="number" step="0.10" value={(f.price_cents / 100).toFixed(2)} onChange={(e) => setF({ ...f, price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="prod-price" /></div>
            <div><Label>IVA %</Label><Input type="number" value={f.vat_rate} onChange={(e) => setF({ ...f, vat_rate: parseFloat(e.target.value) || 10 })} /></div>
            <div><Label>Reparto</Label>
              <Select value={f.department || "bar"} onValueChange={(v) => setF({ ...f, department: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bar">Bar</SelectItem><SelectItem value="kitchen">Cucina</SelectItem><SelectItem value="beach">Spiaggia</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-ink-500 -mt-1">
            L'ordine di visualizzazione si gestisce trascinando il prodotto nella lista principale.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.available} onCheckedChange={(v) => setF({ ...f, available: v })} data-testid="prod-avail" /> Disponibile</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.visible} onCheckedChange={(v) => setF({ ...f, visible: v })} data-testid="prod-visible" /> Visibile in menu</label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Gruppi modificatori</Label>
              {isEdit && (
                <div className="flex items-center gap-1">
                  <ApplyTemplatePicker productId={product?.id}
                                       onApplied={onSaved} disabled={!isEdit} />
                  {(f.modifier_group_ids || []).length > 0 && (
                    <SaveAsTemplateButton productId={product?.id}
                                          productName={f.name}
                                          disabled={!isEdit}
                                          onSaved={onSaved} />
                  )}
                </div>
              )}
            </div>
            <div className="mt-1 grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto rounded-xl border border-sand-300 p-2">
              {modifiers.filter((m) => !m.archived).map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`prod-mod-${m.id}`}>
                  <input type="checkbox" checked={f.modifier_group_ids.includes(m.id)} onChange={() => toggleMod(m.id)} />
                  {m.name} <span className="text-xs text-ink-500">{m.required ? "· obb." : ""}</span>
                </label>
              ))}
              {modifiers.length === 0 && <div className="text-xs text-ink-500 col-span-2 text-center py-3">Nessun gruppo. Creane uno dalla sezione "Gruppi modificatori".</div>}
            </div>
            {f.modifier_group_ids.length > 0 && <div className="text-xs text-ink-500 mt-1">{f.modifier_group_ids.length} gruppo/i associati.</div>}
            {!isEdit && (
              <div className="text-[11px] text-ink-500 mt-1">
                💡 Applica un template dopo aver salvato il prodotto.
              </div>
            )}
          </div>
          <MultiTimeSlotFields slots={f.time_slots || []}
                               onChange={(next) => setF({ ...f, time_slots: next, available_from: null, available_to: null, days: null })}
                               testidPrefix="prod" />

          <div className="rounded-xl border border-sand-300 p-3 bg-sand-50/40" data-testid="prod-schedule">
            <Label className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> Programmazione disponibilità
            </Label>
            <p className="text-[11px] text-ink-500 mt-1">
              Cambio stato automatico. Compila UNO dei due campi (o entrambi). Lascia vuoto per disattivare la programmazione.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs">Disponibile a partire da</Label>
                <Input type="datetime-local" value={toLocalDatetimeInput(f.available_from_at)}
                       onChange={(e) => setF({ ...f, available_from_at: fromLocalDatetimeInput(e.target.value) })}
                       data-testid="prod-available-from-at" />
                <p className="text-[11px] text-ink-500 mt-0.5">Fino a quest'ora → esaurito. Poi torna disponibile.</p>
              </div>
              <div>
                <Label className="text-xs">Esaurito fino a</Label>
                <Input type="datetime-local" value={toLocalDatetimeInput(f.unavailable_until_at)}
                       onChange={(e) => setF({ ...f, unavailable_until_at: fromLocalDatetimeInput(e.target.value) })}
                       data-testid="prod-unavailable-until-at" />
                <p className="text-[11px] text-ink-500 mt-0.5">Fino a quest'ora → esaurito. Poi torna disponibile.</p>
              </div>
            </div>
            {(f.available_from_at || f.unavailable_until_at) && (
              <button type="button" onClick={() => setF({ ...f, available_from_at: null, unavailable_until_at: null })}
                      className="mt-2 text-xs text-bronze-600 hover:underline" data-testid="prod-schedule-clear">
                Rimuovi programmazione
              </button>
            )}
          </div>

          <ProductImagePicker
            imageUrl={f.image_url}
            thumbUrl={f.image_thumb_url}
            productName={f.name}
            onChange={(next) => setF({ ...f, image_url: next.image_url, image_thumb_url: next.image_thumb_url })}
          />

          <WineDataFields
            categories={categories}
            categoryId={f.category_id}
            wine={f.wine}
            badges={f.badges || []}
            onWineChange={(next) => setF({ ...f, wine: next })}
            onBadgesChange={(next) => setF({ ...f, badges: next })}
          />

          {isEdit && <AvailabilityHistorySection productId={product?.id} open={open} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} data-testid="prod-save">{isEdit ? "Salva" : "Crea"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
